// CreateEditEntityModal.tsx
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Stack,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
} from "@mui/material";
import { useState, useEffect, useRef } from "react";
import type { ReactNode } from "react";
import AttachmentManager from "Atoms/AttachmentManager";
import { toast } from "react-toastify";

/* ---------- props ------------------------------------------------------- */
interface Props {
  isEditMode?: boolean;
  open: boolean;
  onClose: () => void;

  /** called when user hits “Save / Update” */
  onSave: (payload: {
    projectName: string;
    projectDesc: string;
    attachments: any[];
    customer?: string;
  }) => void;

  /* attachment list is lifted to parent so it can be reset easily */
  attachments: any[];
  setAttachments: (a: any[]) => void;

  /*  these are **optional** –– if provided we treat the modal as “edit”  */
  initialName?: string;
  initialDesc?: string;
  initialCustomer?: string;
  disableFileChangeForExisting?: boolean; // e.g. true for edit
  isUploading?: boolean; // used to show spinner on save button
  isModel?: boolean; // if true, the show in model , else without model
  dialogTitle?: string;
  nameLabel?: string;
  descriptionLabel?: string;
  saveLabel?: string;
  allowedFileExtensions?: string[];
  extraContent?: ReactNode;
  showCustomerSelect?: boolean;
  customerLabel?: string;
  customerOptions?: Array<{ label: string; value: string }>;
  customerRequired?: boolean;
  isLoadingCustomers?: boolean;
  disableSaveUntilChanged?: boolean;
  existingNames?: string[];
  duplicateNameMessage?: string;
}

/* ---------- component --------------------------------------------------- */
export default function CreateEditEntityModal({
  open,
  onClose,
  onSave,
  attachments,
  setAttachments,
  initialName = "",
  initialDesc = "",
  initialCustomer = "",
  disableFileChangeForExisting = false,
  isEditMode,
  isUploading = false, // used to show spinner on save button
  isModel = false,
  dialogTitle,
  nameLabel = "Project Name",
  descriptionLabel = "Project Description",
  saveLabel,
  allowedFileExtensions = ["pdf"],
  extraContent,
  showCustomerSelect = false,
  customerLabel = "Customer",
  customerOptions = [],
  customerRequired = false,
  isLoadingCustomers = false,
  disableSaveUntilChanged = false,
  existingNames = [],
  duplicateNameMessage,
}: Props) {
  /* local form state */
  const [projectName, setProjectName] = useState(initialName);
  const [projectDesc, setProjectDesc] = useState(initialDesc);
  const [customer, setCustomer] = useState("");
  const nameInputRef = useRef<HTMLInputElement | null>(null);

  /* keep state in sync when a different project is opened for edit */
  useEffect(() => {
    if (open || !isModel) {
      setProjectName(initialName);
      setProjectDesc(initialDesc);
      setCustomer(initialCustomer);
    }
  }, [open, isModel, initialName, initialDesc, initialCustomer]);

  /* save handler */
  const handleSave = () => {
    const normalizedProjectName = projectName?.trim().toLowerCase();

    // Create dialogs validate against the current list before calling the API.
    const hasDuplicateName =
      !isEditMode &&
      existingNames.some(
        (name) => name?.trim().toLowerCase() === normalizedProjectName
      );

    if (hasDuplicateName) {
      toast.error(duplicateNameMessage || `${nameLabel} already exists`);
      nameInputRef.current?.focus();
      return;
    }

    onSave({
      projectName: projectName?.trim(),
      projectDesc: projectDesc?.trim(),
      attachments,
      customer: customer?.trim() || undefined,
    });
  };

  /* clear when closing (only relevant for “create”) */
  const handleClose = () => {
    if (!initialName && !initialDesc) {
      setProjectName("");
      setProjectDesc("");
      setCustomer("");
      setAttachments([]);
    }
    onClose();
  };

  const normalizedInitialCustomer = initialCustomer?.trim() || "";
  const hasEditableFieldChanged =
    projectName?.trim() !== initialName?.trim() ||
    projectDesc?.trim() !== initialDesc?.trim() ||
    (showCustomerSelect && customer?.trim() !== normalizedInitialCustomer);

  const isSaveDisabled =
    !projectName?.trim() ||
    isUploading ||
    isLoadingCustomers ||
    (showCustomerSelect && customerRequired && !customer) ||
    (disableSaveUntilChanged && !hasEditableFieldChanged);

  const renderCustomerSelect = () =>
    showCustomerSelect ? (
      <FormControl fullWidth required={customerRequired}>
        <InputLabel>{customerLabel}</InputLabel>
        <Select
          label={customerLabel}
          value={customer}
          onChange={(event) => setCustomer(String(event.target.value))}
          disabled={isLoadingCustomers}
        >
          {customerOptions.map((option) => (
            <MenuItem key={option.value} value={option.value}>
              {option.label}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    ) : null;

  return (
    <>
      {isModel ? (
        <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
          <DialogTitle>
            {dialogTitle || (isEditMode ? "Edit Project" : "Create Project")}
          </DialogTitle>

          <DialogContent dividers>
            <Stack spacing={2}>
              <TextField
                inputRef={nameInputRef}
                label={nameLabel}
                value={projectName}
                onChange={(e) => setProjectName(e.target.value)}
                fullWidth
                required
              />

              {renderCustomerSelect()}

              <TextField
                label={descriptionLabel}
                value={projectDesc}
                onChange={(e) => setProjectDesc(e.target.value)}
                fullWidth
                multiline
                minRows={3}
              />

              {extraContent}

              <AttachmentManager
                _attachments={attachments}
                onChange={setAttachments}
                disableFileChangeForExisting={disableFileChangeForExisting}
                allowedFileExtensions={allowedFileExtensions}
              />
            </Stack>
          </DialogContent>

          <DialogActions sx={{ p: 3 }}>
            <Button onClick={handleClose} variant="outlined">
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleSave}
              disabled={isSaveDisabled}
            >
              {isUploading ? (
                <CircularProgress
                  size={24}
                  color="inherit"
                  sx={{ marginRight: 1 }}
                />
              ) : (
                ""
              )}

              {saveLabel || (isEditMode ? "Update" : "Create")}
            </Button>
          </DialogActions>
        </Dialog>
      ) : (
        <>
          <Stack spacing={2}>
            <TextField
              inputRef={nameInputRef}
              label={nameLabel}
              value={projectName}
              onChange={(e) => setProjectName(e.target.value)}
              fullWidth
              required
            />

            {renderCustomerSelect()}

            <TextField
              label={descriptionLabel}
              value={projectDesc}
              onChange={(e) => setProjectDesc(e.target.value)}
              fullWidth
              multiline
              minRows={3}
            />

            {extraContent}

            <AttachmentManager
              _attachments={attachments}
              onChange={setAttachments}
              disableFileChangeForExisting={disableFileChangeForExisting}
              allowedFileExtensions={allowedFileExtensions}
            />

            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "end",
              }}
            >
              <Button
                variant="contained"
                onClick={handleSave}
                sx={{ width: "10%" }}
                disabled={isSaveDisabled}
              >
                {isUploading ? (
                  <CircularProgress
                    size={24}
                    color="inherit"
                    sx={{ marginRight: 1 }}
                  />
                ) : (
                  ""
                )}

                {saveLabel || (isEditMode ? "Update" : "Create")}
              </Button>
            </div>
          </Stack>
        </>
      )}
    </>
  );
}
